/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, January 10th 2022, 2:18:17 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { Typography, IconText } from "ui-widgets";
import {
  ServiceBuilderSelectorDialog,
  ServiceBuilderSelectorDialogProps,
} from "service-builder/components/core/ServiceBuilderSelectorDialog";
import { COLORSCHEME } from "theme/styles";
import BPMNTaskIcon from "@iconify/icons-bpmn/service";
import { NODE_TYPES } from "lib/schemas";
import Grid from "@mui/material/Grid";
import { Icon as BPMNIcon } from "@iconify/react";
import { TaskTypeSelectorButton } from "./TaskTypeSelectorButton";

interface BPMNTaskTypeSelectorProps {
  handleTaskTypeSelection: (taskType: keyof typeof NODE_TYPES, taskTitle: string) => void;
  hideStartTaskTypeOption?: boolean;
}

type BPMNPopUpTaskTypeSelectorProps = BPMNTaskTypeSelectorProps &
  Pick<ServiceBuilderSelectorDialogProps, "onClose" | "open">;

export const BPMNPopUpTaskTypeSelector = (props: BPMNPopUpTaskTypeSelectorProps) => {
  return (
    <ServiceBuilderSelectorDialog
      popUpTitle={
        <IconText
          icon={<BPMNIcon icon={BPMNTaskIcon} color={COLORSCHEME.secondary} />}
          textComponent="New Task Template"
        />
      }
      open={props.open}
      onClose={props.onClose}
    >
      <Typography variant="h6">Choose a task type from the options below:</Typography>
      <Grid container sx={{ marginTop: 5 }}>
        <Grid item md={4}>
          <TaskTypeSelectorButton
            bpmnNodeType="View"
            tooltipText="This is a task that requires human interaction."
            onClick={() => props.handleTaskTypeSelection("View", "Perform Action")}
          />
        </Grid>
        <Grid item md={4}>
          <TaskTypeSelectorButton
            bpmnNodeType="Handler"
            tooltipText="This type of task is an automated action such as the generation of a document, sending of an email/sms notification, automated billing etc."
            onClick={() => props.handleTaskTypeSelection("Handler", "Automated Service")}
          />
        </Grid>
        <Grid item md={4}>
          <TaskTypeSelectorButton
            bpmnNodeType="Split"
            tooltipText="This is an automated task that determines the next step(s) in the workflow based on input from the applicant's submission form or worker review forms."
            onClick={() => props.handleTaskTypeSelection("Split", "Auto-Split")}
          />
        </Grid>
        <Grid item md={4}>
          <TaskTypeSelectorButton
            bpmnNodeType="Join"
            tooltipText="This is an automated action that converges multiple steps to a single path in the workflow. An example use case is when multiple users carry out tasks in a workflow (using conditional decisions) and all their tasks must be completed before the workflow process continues."
            onClick={() => props.handleTaskTypeSelection("Join", "Auto-Merge")}
          />
        </Grid>
        <Grid item md={4}>
          <TaskTypeSelectorButton
            bpmnNodeType="Job"
            tooltipText="This is an automated action that you schedule to occur at a certain period(s)"
            onClick={() => props.handleTaskTypeSelection("Job", "Scheduled Job")}
          />
        </Grid>
        {props.hideStartTaskTypeOption ? null : (
          <Grid item md={4}>
            <TaskTypeSelectorButton
              bpmnNodeType="StartFunction"
              tooltipText="This is an automated action designating the beginning of the workflow. If you do not add this to your workflow, it will be automatically added."
              onClick={() => props.handleTaskTypeSelection("StartFunction", "Beginning")}
            />
          </Grid>
        )}
        <Grid item md={4}>
          <TaskTypeSelectorButton
            bpmnNodeType="End"
            tooltipText="This is an automated task designating the termination of the workflow process. You can have an unlimited number of end event tasks."
            onClick={() => props.handleTaskTypeSelection("End", "End")}
          />
        </Grid>
      </Grid>
    </ServiceBuilderSelectorDialog>
  );
};
